import { faFloppyDisks } from '@fortawesome/pro-duotone-svg-icons';
import log from '@/utils/logger';

import { useEffect, useState, useRef } from 'react';
import Button from '@/components/common/Button';
import { createSearchAgent } from '@/api/requests/searchAgents';
import { useRouter } from 'next/navigation';
import { Query } from '@/actions/dtos/search/query';
import { ExecutionInterval } from '@/actions/dtos/search/searchAgent';
import { getSourcePath } from '@/rewrites';
import { useAccount } from '@/hooks/useAccount';
import Modal from '@/components/common/Modal';
import Dropdown from '@/components/common/Dropdown';
import { getOrganizationMembers } from '@/api/requests/organizations';
import { useTranslations } from 'next-intl';

interface CreateAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  query?: Query;
}

const CreateAgentModal: React.FC<CreateAgentModalProps> = ({ query, isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [interval, setInterval] = useState('DAILY');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sliderValue, setSliderValue] = useState(30);
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const router = useRouter();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const { organization, member, refreshCustomer } = useAccount();
  const t = useTranslations();

  useEffect(() => {
    const fetchMembers = async () => {
      if (organization) {
        const members = await getOrganizationMembers(organization.id);
        setOrganizationMembers(members);
      }
    };

    fetchMembers();
  }, [organization]);

  useEffect(() => {
    if (member) {
      setSelectedMembers((prev) => (prev.includes(member.id) ? prev : [...prev, member.id]));
    }
  }, [member]);

  const handleIntervalClick = (newInterval: ExecutionInterval) => {
    setInterval(newInterval);
    if (detailsRef.current) {
      detailsRef.current.open = false;
    }
  };

  const handleMemberChange = (id: string) => {
    setError('');
    setSelectedMembers((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((memberId) => memberId !== id) : [...prevSelected, id],
    );
  };

  const handleSubmit = async () => {
    setError('');
    if (!name.trim()) {
      setError('Bitte geben Sie einen Namen ein.');
      return;
    }

    if (selectedMembers.length === 0) {
      setError('Bitte wählen Sie mindestens ein Teammitglied aus.');
      return;
    }

    try {
      setIsLoading(true);
      await createSearchAgent({
        name: name,
        searchQueryId: query!.id,
        recipientIds: selectedMembers,
        interval,
        notificationsEnabled,
        enabled: true,
        minScore: sliderValue,
      }).then(() => {
        refreshCustomer();
      });

      onClose();
      router.push(getSourcePath('/agents'));
    } catch (error) {
      log.error('Error creating search agent:', error);
      setError('Fehler beim Erstellen des Suchprofils.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderAction = () => (
    <div className="flex w-full items-center justify-between">
      <div className="text-sm text-error">{error && <span>{error}</span>}</div>
      <Button
        onClick={handleSubmit}
        variant="secondary"
        label={t('Button_Save')}
        icon={faFloppyDisks}
        titleClassName="text-sm"
        isLoading={isLoading}
      />
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={'Suchprofil erstellen'} actions={renderAction()}>
      <div className="grid grid-cols-7 items-center gap-4 pb-4 max-sm:grid-cols-2">
        <div className="col-span-2 mt-1 text-sm font-light">Name</div>
        <input
          type="text"
          value={name}
          maxLength={255}
          onChange={(e) => {
            setError('');
            setName(e.target.value);
          }}
          className="input col-span-5 mt-1 h-14 bg-base-200 text-sm"
        />

        <div className="col-span-2 text-sm font-light">Ausführungsintervall</div>
        <Dropdown
          label={ExecutionInterval[interval as keyof typeof ExecutionInterval]}
          className="col-span-5"
          buttonClassName="bg-base-200"
          items={Object.entries(ExecutionInterval).map(([key, value]) => ({
            key,
            value,
            onClick: () => handleIntervalClick(key as ExecutionInterval),
          }))}
        />

        <div className="col-span-2 text-sm font-light">Minimale KI-Bewertung</div>
        <div className="col-span-5 flex items-center gap-4">
          <input
            type="range"
            min={1}
            max={100}
            value={sliderValue}
            onChange={(e) => setSliderValue(parseInt(e.target.value))}
            className="range range-primary range-sm"
          />
          <div className="w-8">{sliderValue}%</div>
        </div>

        <div className="col-span-2 text-sm font-light">Benachrichtigungen</div>
        <input
          type="checkbox"
          className="toggle toggle-primary col-span-5"
          checked={notificationsEnabled}
          onChange={() => setNotificationsEnabled((prev) => !prev)}
        />

        <div className="col-span-2 text-sm font-light">Empfänger</div>
        <div className="card col-span-5 rounded-lg bg-base-200 p-4">
          <ul className="flex max-h-30 flex-col gap-1 overflow-auto">
            {organizationMembers.map((member) => (
              <li key={member.id} className="flex items-center gap-2">
                <label className="flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="checkbox-primary checkbox checkbox-xs"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => handleMemberChange(member.id)}
                  />
                  <span className="px-1 text-sm">{`${member.person.firstname} ${member.person.lastname}`}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default CreateAgentModal;
